import { useEffect, useState } from 'react';
import { Button, Select, Input, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, EmptyState, Chip } from '../../../../shared/components';
import { EditIcon, TrashIcon } from '../../../../shared/icons';
import { getResultsRepository } from '../../../../shared/services/results/repositoryFactory';
import { AccountDetailStagingRow, AccountDetail, AvailableMonth, TipoCuenta, MONTH_NAMES_ES, UpdateAccountDetailInput } from '../../../../shared/services/results/types';

const repo = getResultsRepository();
const MONTH_LABELS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const money = (v: number) => v.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const TIPO_CUENTA_OPTIONS = [{ value: 'INGRESO', label: 'INGRESO' }, { value: 'GASTOS', label: 'GASTOS' }];
/** Estándar de paginación de "Datos Tabla" (ambas vistas: pendiente y final). Se agregaron 5/10/15/30 - se conservan 50/100/200 (Histórico final) y 60 (Pendiente de procesar), no se reemplazan. */
const PAGE_SIZE_OPTIONS = [5, 10, 15, 30, 50, 60, 100, 200];

type EditableRow = { cliente: string; tipoServicio: string; cuentaCodigo: string; cuentaDescripcion: string; tipoCuenta: TipoCuenta; valor: string };
const emptyForm: EditableRow = { cliente: '', tipoServicio: '', cuentaCodigo: '', cuentaDescripcion: '', tipoCuenta: 'INGRESO', valor: '0' };

/**
 * Pestaña "Datos Tabla" - equivalente al histórico acumulado que hoy se
 * arma copiando/pegando cada mes en el Excel. Dos vistas:
 *  - "Pendiente de procesar": staging recién importado, CRUD antes de
 *    confirmar (mismo patrón que StagingReviewTab de Horas Extra).
 *  - "Histórico final": TB_Results_AccountDetail, con paginación
 *    SERVIDOR (5/10/15/30 - no offset infinito, ver justificación en el
 *    resumen entregado) y el toggle "mostrar registros vacíos".
 */
export function AccountDetailTab() {
  const toast = useToast();
  const [subView, setSubView] = useState<'staging' | 'final'>('final');

  // --- meses disponibles / procesar mes ---
  const [months, setMonths] = useState<AvailableMonth[]>([]);
  const [monthToProcess, setMonthToProcess] = useState('');
  const [confirming, setConfirming] = useState<AvailableMonth | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const loadMonths = () => repo.listAvailableMonths().then(setMonths);
  useEffect(() => {
    loadMonths();
  }, []);
  const pendingMonths = months.filter((m) => m.pendingStagingCount > 0);

  const doConfirm = async () => {
    if (!confirming) return;
    setIsConfirming(true);
    try {
      const count = await repo.confirmMonth(confirming.mes, confirming.anio);
      toast.show(`${count} filas de ${confirming.mes} ${confirming.anio} se cargaron al histórico final.`);
      setConfirming(null);
      loadMonths();
      setSubView('final');
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo procesar el mes.', 'error');
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Chip label="Histórico final" active={subView === 'final'} onClick={() => setSubView('final')} />
        <Chip label={`Pendiente de procesar${pendingMonths.length ? ` (${pendingMonths.length})` : ''}`} active={subView === 'staging'} onClick={() => setSubView('staging')} />
      </div>

      {pendingMonths.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <span className="text-sm text-amber-800">Meses con datos importados sin procesar:</span>
          {pendingMonths.map((m) => (
            <Button key={`${m.mes}-${m.anio}`} size="md" variant="outline" onClick={() => setConfirming(m)}>
              Procesar {m.mes} {m.anio} ({m.pendingStagingCount})
            </Button>
          ))}
        </div>
      )}

      {subView === 'staging' ? <StagingView months={months} onProcessed={loadMonths} /> : <FinalView months={months} />}

      <ConfirmDialog
        open={!!confirming}
        title={`Procesar ${confirming?.mes ?? ''} ${confirming?.anio ?? ''}`}
        description="Esto copia todas las filas activas de revisión de este mes al histórico final (Datos Tabla). Si el mes ya estaba cargado antes, se actualiza sin duplicar filas."
        confirmLabel="Procesar mes"
        isLoading={isConfirming}
        onCancel={() => setConfirming(null)}
        onConfirm={doConfirm}
      />
    </div>
  );
}

// ---------------------------------------------------------------------
// Vista "Pendiente de procesar" (staging)
// ---------------------------------------------------------------------
function StagingView({ months, onProcessed }: { months: AvailableMonth[]; onProcessed: () => void }) {
  const toast = useToast();
  const [mes, setMes] = useState('');
  const [rows, setRows] = useState<AccountDetailStagingRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState<AccountDetailStagingRow | null>(null);
  const [form, setForm] = useState<EditableRow>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<AccountDetailStagingRow | null>(null);

  const load = () => {
    setIsLoading(true);
    repo.listStaging({ mes: mes || undefined }).then((r) => {
      setRows(r);
      setIsLoading(false);
    });
  };
  useEffect(() => {
    load();
  }, [mes]);

  const openEdit = (r: AccountDetailStagingRow) => {
    setForm({ cliente: r.cliente, tipoServicio: r.tipoServicio, cuentaCodigo: r.cuentaCodigo, cuentaDescripcion: r.cuentaDescripcion, tipoCuenta: r.tipoCuenta, valor: String(r.valor) });
    setEditing(r);
  };
  const save = async () => {
    if (!editing) return;
    setIsSaving(true);
    try {
      await repo.updateStagingRow(editing.id, { ...form, valor: Number(form.valor) || 0 });
      toast.show('Fila actualizada.');
      setEditing(null);
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const monthOptions = [{ value: '', label: 'Todos los meses' }, ...months.map((m) => ({ value: m.mes, label: `${m.mes.toUpperCase()} ${m.anio}` }))];

  const columns: Column<AccountDetailStagingRow>[] = [
    { key: 'cliente', header: 'Cliente', render: (r) => r.cliente, sortable: true, sortValue: (r) => r.cliente },
    { key: 'tipoServicio', header: 'Tipo Servicio', render: (r) => r.tipoServicio },
    { key: 'cuenta', header: 'Cuenta', render: (r) => `${r.cuentaCodigo} · ${r.cuentaDescripcion}` },
    { key: 'tipoCuenta', header: 'Tipo Cuenta', render: (r) => <Badge tone={r.tipoCuenta === 'INGRESO' ? 'info' : 'warning'}>{r.tipoCuenta}</Badge> },
    { key: 'valor', header: 'Valor', render: (r) => money(r.valor), sortable: true, sortValue: (r) => r.valor },
    { key: 'status', header: 'Estado', render: (r) => <Badge tone={r.status === 'Procesado' ? 'success' : r.status === 'Revisado' ? 'info' : 'neutral'}>{r.status}</Badge> },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.status === 'Procesado' ? null : (
          <div className="flex gap-1">
            <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(r)} className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
            <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(r)} className="h-9 w-9 text-critical hover:bg-red-50" />
          </div>
        ),
    },
  ];

  return (
    // Excepción deliberada al layout móvil-first del resto de la Suite (ver
    // "Tabla Resumen" para el mismo patrón): esta vista se muestra a todo lo
    // ancho de la pantalla en computador.
    <div className="relative left-1/2 -ml-[50vw] w-screen space-y-4 px-4 sm:px-6 lg:px-8">
      <Select label="Mes" options={monthOptions} value={mes} onChange={(e) => setMes(e.target.value)} className="max-w-[240px]" />
      {rows.length === 0 && !isLoading ? (
        <EmptyState icon="🗂️" title="Sin filas pendientes" description='Carga un archivo desde "Resultados del Mes" para empezar.' />
      ) : (
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} isLoading={isLoading} pageSize={5} pageSizeOptions={PAGE_SIZE_OPTIONS} emptyTitle="Sin filas" scrollClassName="scroll-accent" />
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Editar fila">
        <div className="space-y-3">
          <Input label="Cliente" value={form.cliente} onChange={(e) => setForm((f) => ({ ...f, cliente: e.target.value }))} />
          <Input label="Tipo Servicio" value={form.tipoServicio} onChange={(e) => setForm((f) => ({ ...f, tipoServicio: e.target.value }))} />
          <Input label="Código de cuenta" value={form.cuentaCodigo} onChange={(e) => setForm((f) => ({ ...f, cuentaCodigo: e.target.value }))} />
          <Input label="Descripción de cuenta" value={form.cuentaDescripcion} onChange={(e) => setForm((f) => ({ ...f, cuentaDescripcion: e.target.value }))} />
          <Select label="Tipo Cuenta" options={TIPO_CUENTA_OPTIONS} value={form.tipoCuenta} onChange={(e) => setForm((f) => ({ ...f, tipoCuenta: e.target.value as TipoCuenta }))} />
          <Input label="Valor" type="number" value={form.valor} onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))} />
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={() => setEditing(null)}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Eliminar fila"
        description={`¿Eliminar la fila de "${deleting?.cliente}" - ${deleting?.cuentaCodigo}? No afecta el histórico final si este mes ya fue procesado antes.`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) {
            await repo.deleteStagingRow(deleting.id);
            onProcessed();
          }
          setDeleting(null);
          load();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------
// Vista "Histórico final" (Datos Tabla) - paginación SERVIDOR 5/10/15/30
// ---------------------------------------------------------------------
function FinalView({ months }: { months: AvailableMonth[] }) {
  const toast = useToast();
  const [mes, setMes] = useState('');
  const [anio, setAnio] = useState('');
  const [cliente, setCliente] = useState('');
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [autoSelected, setAutoSelected] = useState(false);

  // Al entrar (o en cuanto `months` termina de cargar), preselecciona el mes
  // y año más reciente disponible - mismo criterio cronológico que
  // "Resultados del Mes" - en vez de arrancar en "Todos". Solo se hace una
  // vez, para no pisar un filtro que el usuario ya cambió a mano.
  useEffect(() => {
    if (autoSelected || months.length === 0) return;
    const latest = months.reduce<AvailableMonth | null>((acc, m) => {
      if (!acc) return m;
      if (m.anio !== acc.anio) return m.anio > acc.anio ? m : acc;
      return MONTH_NAMES_ES.indexOf(m.mes) > MONTH_NAMES_ES.indexOf(acc.mes) ? m : acc;
    }, null);
    if (latest) {
      setMes(latest.mes);
      setAnio(String(latest.anio));
    }
    setAutoSelected(true);
  }, [months, autoSelected]);
  const [rows, setRows] = useState<AccountDetail[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState<AccountDetail | null>(null);
  const [form, setForm] = useState<EditableRow>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<AccountDetail | null>(null);

  const load = () => {
    setIsLoading(true);
    repo
      .listAccountDetail({ mes: mes || undefined, anio: anio ? Number(anio) : undefined, cliente: cliente || undefined, includeEmpty, page, pageSize })
      .then((r) => {
        setRows(r.items);
        setTotal(r.total);
        setIsLoading(false);
      });
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, anio, cliente, includeEmpty, page, pageSize]);
  useEffect(() => setPage(1), [mes, anio, cliente, includeEmpty]);

  const openEdit = (r: AccountDetail) => {
    setForm({ cliente: r.cliente, tipoServicio: r.tipoServicio, cuentaCodigo: r.cuentaCodigo, cuentaDescripcion: r.cuentaDescripcion, tipoCuenta: r.tipoCuenta, valor: String(r.valor) });
    setEditing(r);
  };
  const save = async () => {
    if (!editing) return;
    setIsSaving(true);
    try {
      const patch: UpdateAccountDetailInput = { ...form, valor: Number(form.valor) || 0 };
      await repo.updateAccountDetail(editing.id, patch);
      toast.show('Registro actualizado.');
      setEditing(null);
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const columns: Column<AccountDetail>[] = [
    { key: 'mes', header: 'Mes', render: (r) => `${r.mes} ${r.anio}` },
    { key: 'cliente', header: 'Cliente', render: (r) => r.cliente },
    { key: 'tipoServicio', header: 'Tipo Servicio', render: (r) => r.tipoServicio },
    { key: 'cuenta', header: 'Cuenta', render: (r) => `${r.cuentaCodigo} · ${r.cuentaDescripcion}` },
    { key: 'tipoCuenta', header: 'Tipo Cuenta', render: (r) => <Badge tone={r.tipoCuenta === 'INGRESO' ? 'info' : 'warning'}>{r.tipoCuenta}</Badge> },
    { key: 'valor', header: 'Valor', render: (r) => money(r.valor) },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(r)} className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(r)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    // Excepción deliberada al layout móvil-first del resto de la Suite (ver
    // "Tabla Resumen" para el mismo patrón): esta vista se muestra a todo lo
    // ancho de la pantalla en computador.
    <div className="relative left-1/2 -ml-[50vw] w-screen space-y-4 px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Select label="Mes" options={[{ value: '', label: 'Todos' }, ...MONTH_NAMES_ES.map((m, i) => ({ value: m, label: MONTH_LABELS[i] }))]} value={mes} onChange={(e) => setMes(e.target.value)} />
        <Input label="Año" type="number" placeholder="Todos" value={anio} onChange={(e) => setAnio(e.target.value)} />
        <Input label="Cliente" placeholder="Todos" value={cliente} onChange={(e) => setCliente(e.target.value)} />
        <label className="flex items-end gap-2 pb-3 text-sm text-ink-600">
          <input type="checkbox" checked={includeEmpty} onChange={(e) => setIncludeEmpty(e.target.checked)} className="h-4 w-4 rounded border-ink-300" />
          Mostrar registros vacíos (Valor = 0)
        </label>
      </div>

      {rows.length === 0 && !isLoading ? (
        <EmptyState icon="📄" title="Sin registros" description="Ajusta los filtros o procesa un mes desde la pestaña anterior." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-ink-100 bg-surface">
          <div className="scroll-accent overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 bg-ink-50/60 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                  {columns.map((c) => (
                    <th key={c.key} className="whitespace-nowrap px-4 py-3">
                      {c.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-ink-50 last:border-0 hover:bg-ink-50/40">
                    {columns.map((c) => (
                      <td key={c.key} className="whitespace-nowrap px-4 py-3 text-ink-700">
                        {c.render(r)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Paginador SERVIDOR: cada click trae la página siguiente del backend (no offset en cliente),
              indispensable con miles de filas por mes - ver justificación entregada. */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-100 px-4 py-3 text-sm text-ink-500">
            <span>
              Página {page} de {totalPages} · {total.toLocaleString('es-CO')} registros
            </span>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs">
                Ver
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="rounded-lg border border-ink-200 bg-surface px-2 py-1 text-xs font-medium text-ink-700 outline-none focus:ring-2 focus:ring-brand-200"
                >
                  {PAGE_SIZE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                por página
              </label>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg px-3 py-1.5 disabled:opacity-40 hover:bg-ink-50">
                  Anterior
                </button>
                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg px-3 py-1.5 disabled:opacity-40 hover:bg-ink-50">
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Editar registro">
        <div className="space-y-3">
          <Input label="Cliente" value={form.cliente} onChange={(e) => setForm((f) => ({ ...f, cliente: e.target.value }))} />
          <Input label="Tipo Servicio" value={form.tipoServicio} onChange={(e) => setForm((f) => ({ ...f, tipoServicio: e.target.value }))} />
          <Input label="Código de cuenta" value={form.cuentaCodigo} onChange={(e) => setForm((f) => ({ ...f, cuentaCodigo: e.target.value }))} />
          <Input label="Descripción de cuenta" value={form.cuentaDescripcion} onChange={(e) => setForm((f) => ({ ...f, cuentaDescripcion: e.target.value }))} />
          <Select label="Tipo Cuenta" options={TIPO_CUENTA_OPTIONS} value={form.tipoCuenta} onChange={(e) => setForm((f) => ({ ...f, tipoCuenta: e.target.value as TipoCuenta }))} />
          <Input label="Valor" type="number" value={form.valor} onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))} />
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={() => setEditing(null)}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Eliminar registro"
        description={`¿Eliminar el registro de "${deleting?.cliente}" - ${deleting?.cuentaCodigo} (${deleting?.mes} ${deleting?.anio})? Esta acción es un borrado lógico, reversible desde base de datos si fuera necesario.`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await repo.deleteAccountDetail(deleting.id);
          setDeleting(null);
          load();
        }}
      />
    </div>
  );
}
